import { useEffect, useState } from "react";
import { TourPlayer } from "@/components/TourPlayer";
import { useNavigate, Link } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Tag, Loader2, Heart, Landmark, Wallet } from "lucide-react";
import { BankTransferDialog } from "@/components/BankTransferDialog";
import { toast } from "sonner";

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [promos, setPromos] = useState([]);
  const [busy, setBusy] = useState("");
  const [seats, setSeats] = useState(3);
  const [local, setLocal] = useState({ enabled: false });
  const [cards, setCards] = useState({ enabled: false });
  const [momo, setMomo] = useState({ enabled: false });
  const [intent, setIntent] = useState(null);

  useEffect(() => {
    api.get("/plans").then(({ data }) => setPlans(data));
    api.get("/promotions/active").then(({ data }) => setPromos(data)).catch(() => {});
    api.get("/localpay/config").then(({ data }) => setLocal(data)).catch(() => {});
    api.get("/lemonsqueezy/config").then(({ data }) => setCards(data)).catch(() => {});
    api.get("/momo/config").then(({ data }) => setMomo(data)).catch(() => {});
  }, []);

  const payWithMomo = async (plan) => {
    if (!user) return nav("/signup");
    setBusy(plan.id);
    try {
      const { data } = await api.post("/momo/create", {
        kind: "plan",
        plan_id: plan.id,
        origin_url: window.location.origin,
        ...(plan.per_seat ? { seats } : {}),
      });
      window.location.href = data.pay_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy("");
    }
  };

  const payByTransfer = async (plan) => {
    if (!user) return nav("/signup");
    setBusy(plan.id);
    try {
      const { data } = await api.post("/localpay/intent", {
        kind: "plan",
        plan_id: plan.id,
        milu_session: localStorage.getItem("chatmilu.milu.session") || undefined,
        ...(plan.per_seat ? { seats } : {}),
      });
      setIntent({ ...data });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const upgrade = async (plan) => {
    if (!user) return nav("/signup");
    if (plan.price === 0) return toast.info("You are already on the Free plan features.");
    setBusy(plan.id);
    try {
      const path = cards.enabled ? "/lemonsqueezy/checkout" : "/payments/checkout";
      const { data } = await api.post(path, {
        ...(cards.enabled ? { kind: "plan" } : {}),
        plan_id: plan.id,
        origin_url: window.location.origin,
        milu_session: localStorage.getItem("chatmilu.milu.session") || undefined,
        ...(plan.per_seat ? { seats } : {}),
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      setBusy("");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 py-16 sm:px-8" data-testid="pricing-page">
      <p className="text-xs font-semibold uppercase tracking-widest text-brand">Plans</p>
      <h1 className="mt-3 font-display text-[1.8rem] font-extrabold tracking-tighter sm:text-[2.4rem]">
        Upgrade My Conversations
      </h1>
      <p className="mt-3 max-w-xl text-sm text-ink-soft md:text-base">
        Every plan speaks all 50 languages. Paid plans unlock more AI, more rooms and deeper insights.
      </p>

      <div className="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:items-center"
           data-testid="pricing-tour">
        <TourPlayer />
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-brand">
            Not sure yet?
          </p>
          <p className="mt-2 font-display text-xl font-bold leading-snug md:text-2xl">
            See everything you are paying for, in one minute
          </p>
          <p className="mt-2 text-sm text-ink-soft">
            MiLu shows you the chat, the translated rooms, the newsroom she reads aloud, the
            phrasebook and the calendar — narrated in your own language. Jump straight to whichever
            part you came for.
          </p>
          <Link
            to="/tour"
            data-testid="pricing-tour-link"
            className="mt-4 inline-flex min-h-[38px] items-center gap-1.5 rounded-full border border-white/15 px-4 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
          >
            Open the tour on its own page
          </Link>
        </div>
      </div>

      {promos.length > 0 && (
        <div className="mt-8 flex flex-wrap gap-3" data-testid="pricing-promos">
          {promos.map((p) => (
            <span
              key={p.promo_id}
              className="inline-flex items-center gap-2 rounded-full border border-forest/30 bg-forest/10 px-4 py-2 text-sm"
            >
              <Tag className="h-3.5 w-3.5 text-forest" />
              <b>{p.code}</b> · {p.description}
            </span>
          ))}
        </div>
      )}

      <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4 lg:gap-6">
        {plans.map((p, i) => (
          <div
            key={p.id}
            data-testid={`plan-card-${p.id}`}
            style={{ animationDelay: `${i * 80}ms` }}
            className={`rise flex flex-col rounded-3xl border p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-md ${
              p.id === "premium" ? "border-brand/40 bg-panel" : "border-white/10 bg-panel"
            }`}
          >
            {p.id === "premium" && (
              <span className="mb-4 inline-flex w-fit rounded-full bg-brand px-3 py-1 text-xs font-semibold text-white">
                Most popular
              </span>
            )}
            {p.per_seat && (
              <span className="mb-4 inline-flex w-fit rounded-full bg-forest/20 px-3 py-1 text-xs font-semibold text-forest">
                For companies
              </span>
            )}
            <p className="font-display text-2xl font-bold tracking-tight">{p.name}</p>
            <p className="mt-1 text-sm text-ink-soft">{p.tagline}</p>
            <p className="mt-6 font-display text-4xl font-extrabold tracking-tighter">
              ${p.price}
              <span className="text-base font-medium text-ink-soft"> /{p.period}</span>
            </p>
            {p.per_seat && (
              <div className="mt-4">
                <label className="text-xs uppercase tracking-widest text-ink-soft">Seats</label>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    data-testid="seats-decrease"
                    onClick={() => setSeats((s) => Math.max(p.min_seats || 3, s - 1))}
                    className="h-9 w-9 rounded-full bg-white/10 text-lg leading-none hover:bg-white/20"
                  >
                    −
                  </button>
                  <span data-testid="seats-count" className="w-10 text-center font-semibold">
                    {seats}
                  </span>
                  <button
                    data-testid="seats-increase"
                    onClick={() => setSeats((s) => Math.min(200, s + 1))}
                    className="h-9 w-9 rounded-full bg-white/10 text-lg leading-none hover:bg-white/20"
                  >
                    +
                  </button>
                  <span data-testid="seats-total" className="ms-2 text-sm text-ink-soft">
                    = ${(seats * p.price).toFixed(2)} / month
                  </span>
                </div>
              </div>
            )}
            <ul className="mt-6 flex-1 space-y-3 text-sm">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest" />
                  <span className="text-ink-soft">{f}</span>
                </li>
              ))}
            </ul>
            <Button
              data-testid={`plan-cta-${p.id}`}
              onClick={() => upgrade(p)}
              disabled={busy === p.id || (user?.plan === p.id && !p.per_seat)}
              className={`mt-8 min-h-[48px] w-full rounded-full active:scale-95 ${
                p.id === "premium" ? "bg-brand hover:bg-brand-hover" : "bg-white/10 hover:bg-white/20"
              }`}
            >
              {busy === p.id && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              {p.per_seat
                ? `Buy ${seats} seats`
                : user?.plan === p.id
                ? "Current plan"
                : p.price === 0
                ? "Included"
                : `Get ${p.name}`}
            </Button>
            {local.enabled && p.price > 0 && (
              <button
                data-testid={`plan-bank-${p.id}`}
                onClick={() => payByTransfer(p)}
                className="mt-3 inline-flex items-center justify-center gap-2 text-xs font-medium text-ink-soft transition-colors hover:text-brand"
              >
                <Landmark className="h-3.5 w-3.5" /> Or pay by Vietnamese bank transfer
              </button>
            )}
            {momo.enabled && p.price > 0 && (
              <button
                data-testid={`plan-momo-${p.id}`}
                onClick={() => payWithMomo(p)}
                className="mt-2 inline-flex items-center justify-center gap-2 text-xs font-medium text-ink-soft transition-colors hover:text-brand"
              >
                <Wallet className="h-3.5 w-3.5" /> Or pay with MoMo
              </button>
            )}
          </div>
        ))}
      </div>

      <p className="mt-10 text-xs text-ink-soft">
        {cards.enabled
          ? "Card payments are live through Lemon Squeezy, our merchant of record."
          : "Test payments use Stripe test mode — card 4242 4242 4242 4242, any future expiry, any CVC."}
      </p>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-5 rounded-3xl border border-white/10 bg-panel p-7">
        <div>
          <p className="font-display text-lg font-bold tracking-tight">Not ready for a plan?</p>
          <p className="mt-1 text-sm text-ink-soft">
            A one-time donation keeps the free tier free for people who can't pay.
          </p>
        </div>
        <Link to="/support">
          <Button
            data-testid="pricing-goto-support"
            variant="outline"
            className="min-h-[44px] rounded-full px-6"
          >
            <Heart className="me-2 h-4 w-4 text-brand" /> Support ChatMiLu
          </Button>
        </Link>
      </div>

      {intent && <BankTransferDialog intent={intent} onClose={() => setIntent(null)} />}
    </div>
  );
}
